// D-1046: 画面利用計測のまとめ送り。
//   ★書き込み経路は D-068 の fn_track_page_usage(trackPageUsage)をそのまま使う。新しい RPC・表は作らない。
//   ★タップや画面遷移のたびに通信しない。メモリ上で (staffId, pageKey) ごとに views / seconds を足し込み、
//     最初の記録から USAGE_FLUSH_DELAY_MS 後にまとめて送る(1 回の送信で key ごとに 1 RPC)。
//     画面が隠れた(visibilitychange=hidden / pagehide)ときはすぐ送る(iOS がそのまま殺すことがあるため)。
//   ★圏外(navigator.onLine === false)では送らずに溜めておき、online に戻ったら送る。
//     送信の失敗は捨てる(届いたのに応答だけ落ちた場合に二重に数えないため。再送しない・端末に保存しない)。
//   ★例外を投げない・await させない。計測が画面を止めてはいけない(D-524 と同じ方針)。
import { trackPageUsage } from './pageUsage'

export const USAGE_FLUSH_DELAY_MS = 30_000
// 溜める上限(key の数)。ルート数 + flow で 150 程度なので普段は届かない。超えた分は黙って捨てる。
export const USAGE_BUFFER_MAX_ENTRIES = 300

/** @type {Map<string, { staffId: string, pageKey: string, views: number, seconds: number }>} */
const buffer = new Map()
let timer = null
let listenersInstalled = false

const isOffline = () => typeof navigator !== 'undefined' && navigator.onLine === false

function schedule() {
  if (timer != null) return
  timer = setTimeout(() => { timer = null; flushUsage() }, USAGE_FLUSH_DELAY_MS)
}

/**
 * 利用を 1 件足し込む(送信はしない)。
 * @param {string} staffId
 * @param {string} pageKey
 * @param {{ views?: number, seconds?: number }} add
 */
export function recordUsage(staffId, pageKey, { views = 0, seconds = 0 } = {}) {
  try {
    if (!staffId || !pageKey) return
    const v = Math.max(0, Math.floor(Number(views) || 0))
    const s = Math.max(0, Math.floor(Number(seconds) || 0))
    if (v === 0 && s === 0) return
    const id = `${staffId}\u0000${pageKey}`
    const cur = buffer.get(id)
    if (cur) {
      cur.views += v
      cur.seconds += s
    } else {
      if (buffer.size >= USAGE_BUFFER_MAX_ENTRIES) return
      buffer.set(id, { staffId, pageKey, views: v, seconds: s })
    }
    schedule()
  } catch {
    /* 計測は本業を止めない */
  }
}

/**
 * 溜まった分を送る。圏外なら送らずに残す(online で再度呼ばれる)。
 * 戻り値は送った RPC の数(テスト用)。呼び出し側は await しなくてよい。
 */
export function flushUsage() {
  try {
    if (timer != null) { clearTimeout(timer); timer = null }
    if (buffer.size === 0) return 0
    if (isOffline()) return 0
    const rows = [...buffer.values()]
    buffer.clear()
    for (const r of rows) {
      try {
        Promise.resolve(
          trackPageUsage({ staffId: r.staffId, pageKey: r.pageKey, addViews: r.views, addSeconds: r.seconds })
        ).catch(() => {})
      } catch {
        /* 同期例外も握る */
      }
    }
    return rows.length
  } catch {
    return 0
  }
}

/**
 * online に戻ったときに送る見張りを 1 回だけ付ける。
 * ★hidden / pagehide での送信は呼び出し側(useRouteUsageTracking)が「滞在秒を足してから」行う。
 *   ここで先に送ると、その時点の滞在秒が次回送りになり、iOS に殺されたとき失われるため。
 */
export function installUsageFlushListeners() {
  if (listenersInstalled || typeof window === 'undefined') return
  listenersInstalled = true
  try {
    window.addEventListener('online', () => flushUsage())
  } catch {
    /* noop */
  }
}

/** テスト用: 状態を空に戻す。 */
export function _resetUsageBufferForTest() {
  if (timer != null) clearTimeout(timer)
  timer = null
  buffer.clear()
}

/** テスト用: 溜まっている中身。 */
export function _peekUsageBufferForTest() {
  return [...buffer.values()].map(r => ({ ...r }))
}
